import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  Alert,
  AlertDescription,
  AlertIcon,
  Box,
  Button,
  Center,
  Drawer,
  DrawerBody,
  DrawerContent,
  DrawerFooter,
  DrawerHeader,
  DrawerOverlay,
  FormControl,
  FormLabel,
  HStack,
  Icon,
  Image,
  Input,
  Modal,
  ModalBody,
  ModalContent,
  ModalOverlay,
  Text,
  VStack,
} from "@chakra-ui/react";
import { useNavigate } from "react-router-dom";
import { FaRegCalendarAlt } from "react-icons/fa";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { api } from "../../Backend/api";

// ─── Constants ────────────────────────────────────────────────────────────────
const CLOUDFRONT_BASE_URL = "https://dl6ri8ttf37m3.cloudfront.net";

const VIEW_FORM = "form";
const VIEW_CONFIRM = "confirm";
const VIEW_PICK = "pick";
const VIEW_NOT_FOUND = "notFound";
const VIEW_ERROR = "error";

const HEADER_BY_VIEW = {
  [VIEW_FORM]: "Look up your booking",
  [VIEW_CONFIRM]: "Is this your booking?",
  [VIEW_PICK]: "Which booking is yours?",
  [VIEW_NOT_FOUND]: "No match found",
  [VIEW_ERROR]: "Connection issue",
};

// ─── Helpers ──────────────────────────────────────────────────────────────────
// `room.images[i]` is either a full https URL (Hostaway/S3 public) or a bare
// S3 key from a self-uploaded property. Detect with startsWith('http') per the
// API contract; fall back to the CDN otherwise.
const buildRoomImageUrl = (raw) => {
  if (!raw || typeof raw !== "string") return null;
  if (raw.startsWith("http")) return raw;
  return `${CLOUDFRONT_BASE_URL}/public/${encodeURIComponent(raw)}?width=600&format=webp`;
};

// Local-time YYYY-MM-DD. Avoids the new Date('YYYY-MM-DD') UTC-shift bug by
// using component getters from the picked Date. The contract accepts
// 'YYYY-MM-DD' or full ISO; this is the more compact one.
const toIsoDate = (d) => {
  if (!d) return "";
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

// Display formatter for the confirmation card. Accepts 'YYYY-MM-DD' or full
// ISO strings from the BE. Avoids parsing 'YYYY-MM-DD' through new Date()
// (which interprets it as UTC midnight and drifts in negative-offset zones).
const formatDateForDisplay = (raw, timezone) => {
  if (!raw) return "";
  try {
    const dateOnly = String(raw).split("T")[0];
    const [y, m, d] = dateOnly.split("-").map(Number);
    if (!y || !m || !d) return raw;
    const date = new Date(y, m - 1, d);
    return new Intl.DateTimeFormat("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
      timeZone: timezone || undefined,
    }).format(date);
  } catch {
    return raw;
  }
};

// Dismiss the on-screen keyboard before opening the calendar. Without this,
// the iOS/Android keyboard from the Name input stays up and clips the
// bottom of the calendar grid.
const dismissActiveKeyboard = () => {
  if (typeof document === "undefined") return;
  const el = document.activeElement;
  if (el && typeof el.blur === "function") {
    el.blur();
  }
};

// Localized "MMM d, yyyy" for the date button label. Independent from the
// confirmation card formatter — that one operates on raw strings from BE.
const formatDateLabel = (date) =>
  date
    ? new Intl.DateTimeFormat("en-US", {
        month: "short",
        day: "numeric",
        year: "numeric",
      }).format(date)
    : "";

// ─── Date selector button ─────────────────────────────────────────────────────
// Standalone Chakra-styled button that triggers the calendar modal. Replaces
// the previous react-datepicker customInput pattern — withPortal kept losing
// the stacking war against the drawer's modal context, so the calendar
// itself now lives inside a sibling Chakra <Modal> (see PickerModal below).
const DateButton = ({ value, onClick, placeholder, disabled }) => {
  const handleOpen = () => {
    if (disabled) return;
    dismissActiveKeyboard();
    onClick?.();
  };

  return (
    <Box
      onClick={handleOpen}
      role="button"
      tabIndex={disabled ? -1 : 0}
      onKeyDown={(e) => {
        if (disabled) return;
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          handleOpen();
        }
      }}
      px={3}
      h="48px"
      display="flex"
      alignItems="center"
      gap={2}
      bg="#2d3748"
      borderRadius="10px"
      border="1px solid"
      borderColor="rgba(255,255,255,0.08)"
      cursor={disabled ? "not-allowed" : "pointer"}
      opacity={disabled ? 0.5 : 1}
      transition="all 0.15s ease"
      _hover={
        disabled
          ? {}
          : { bg: "#374152", borderColor: "rgba(255,255,255,0.18)" }
      }
      _focusVisible={{
        outline: "none",
        borderColor: "#817EFF",
        boxShadow: "0 0 0 1px #817EFF",
      }}
      minW={0}
    >
      <Icon
        as={FaRegCalendarAlt}
        color={value ? "#817EFF" : "gray.500"}
        fontSize="13px"
        flexShrink={0}
      />
      <Text
        color={value ? "white" : "gray.500"}
        fontSize="14px"
        fontWeight={500}
        noOfLines={1}
        minW={0}
      >
        {value || placeholder}
      </Text>
    </Box>
  );
};

// ─── Calendar modal ───────────────────────────────────────────────────────────
// A Chakra Modal hosting an inline react-datepicker. Chakra's modal manager
// handles z-index correctly relative to the parent Drawer (drawer = base
// modal, picker = nested modal — Chakra stacks them). No portal hacks.
//
// The sx block below restyles react-datepicker's default white/small look
// into the app's dark palette (#1a202c surface, #817EFF accent) and scales
// the day grid up to 42px touch targets (default is ~27px — too cramped on
// mobile, where this drawer is primarily used). Scoped to this modal via
// the wrapping Box, so no global CSS and no effect on any other
// react-datepicker usage that might land in the app later.
const CALENDAR_SX = {
  // Root panel
  ".react-datepicker": {
    backgroundColor: "#1a202c",
    border: "1px solid rgba(255,255,255,0.1)",
    borderRadius: "18px",
    boxShadow: "0 24px 60px rgba(0,0,0,0.55)",
    fontFamily: "inherit",
    padding: "10px 8px 12px",
  },
  ".react-datepicker__triangle": { display: "none" },
  // Header / month label
  ".react-datepicker__header": {
    backgroundColor: "transparent",
    borderBottom: "1px solid rgba(255,255,255,0.08)",
    paddingTop: "14px",
    paddingBottom: "10px",
  },
  ".react-datepicker__current-month": {
    color: "white",
    fontSize: "17px",
    fontWeight: 700,
    marginBottom: "10px",
  },
  // Prev/next month arrows — nudged to align with the taller header
  ".react-datepicker__navigation": { top: "18px" },
  ".react-datepicker__navigation-icon::before": {
    borderColor: "#A0AEC0",
    borderWidth: "2px 2px 0 0",
  },
  ".react-datepicker__navigation:hover *::before": {
    borderColor: "#817EFF",
  },
  // Day-of-week labels
  ".react-datepicker__day-name": {
    color: "#A0AEC0",
    width: "42px",
    lineHeight: "30px",
    margin: "1px",
    fontSize: "12px",
    fontWeight: 600,
    textTransform: "uppercase",
    letterSpacing: "0.04em",
  },
  // Day cells — 42px square touch targets
  ".react-datepicker__day": {
    color: "white",
    width: "42px",
    lineHeight: "42px",
    margin: "1px",
    borderRadius: "12px",
    fontSize: "15px",
    fontWeight: 500,
    transition: "background-color 0.12s ease",
  },
  ".react-datepicker__day:hover": {
    backgroundColor: "rgba(129, 126, 255, 0.25)",
    borderRadius: "12px",
  },
  ".react-datepicker__day--selected, .react-datepicker__day--keyboard-selected":
    {
      backgroundColor: "#817EFF",
      color: "white",
      fontWeight: 700,
    },
  ".react-datepicker__day--selected:hover": {
    backgroundColor: "#504CFF",
  },
  ".react-datepicker__day--today:not(.react-datepicker__day--selected)": {
    border: "1px solid rgba(129, 126, 255, 0.55)",
    lineHeight: "40px", // compensate for the 1px border so the cell doesn't grow
  },
  ".react-datepicker__day--disabled, .react-datepicker__day--disabled:hover": {
    color: "rgba(255,255,255,0.22)",
    backgroundColor: "transparent",
    cursor: "not-allowed",
  },
  ".react-datepicker__day--outside-month": {
    color: "rgba(255,255,255,0.3)",
  },
  ".react-datepicker__month": {
    margin: "8px 6px 4px",
  },
};

const PickerModal = ({ isOpen, onClose, selected, onChange, minDate }) => (
  <Modal
    isOpen={isOpen}
    onClose={onClose}
    isCentered
    autoFocus={false}
    returnFocusOnClose={false}
  >
    <ModalOverlay bg="rgba(0,0,0,0.85)" />
    {/* Bulletproof centering: pin the (transparent) content box to the
        full viewport with fixed insets, then flex-center the
        intrinsic-width calendar inside it. This sidesteps Chakra's
        ModalContent margin/maxW defaults entirely — those were leaving
        the calendar visibly off-center because the content box itself
        wasn't symmetric in the viewport. */}
    <ModalContent
      position="fixed"
      top="0"
      left="0"
      right="0"
      bottom="0"
      m={0}
      w="auto"
      maxW="none"
      bg="transparent"
      boxShadow="none"
    >
      <ModalBody
        p={0}
        w="full"
        h="full"
        display="flex"
        justifyContent="center"
        alignItems="center"
        // The full-viewport content box sits over the overlay, so taps
        // outside the calendar land here instead of on ModalOverlay.
        // Close manually when the tap is on the flex container itself
        // (i.e. not inside the calendar) to preserve tap-outside-to-close.
        onClick={(e) => {
          if (e.target === e.currentTarget) onClose();
        }}
        sx={CALENDAR_SX}
      >
        <DatePicker
          inline
          selected={selected}
          onChange={onChange}
          minDate={minDate}
        />
      </ModalBody>
    </ModalContent>
  </Modal>
);

// ─── Component ────────────────────────────────────────────────────────────────
/**
 * LookupDrawer — Part 2 of the QR-driven discovery flow.
 *
 * Internal state machine:
 *   form → confirm → (handoff to /go?bID=…)
 *   form → notFound → (back to form)
 *   form → error → (retry → form)
 *
 * The ONLY data that crosses the seam into the existing bID pipeline is the
 * booking id. Everything else from the lookup payload is dropped at handoff
 * so /bookings/details/:bID is the single source of truth past this point.
 */
export const LookupDrawer = ({ isOpen, onClose, ownerId }) => {
  const navigate = useNavigate();

  const [view, setView] = useState(VIEW_FORM);
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({
    guestName: "",
    checkInDate: null, // Date | null — react-datepicker uses Date instances
    checkOutDate: null,
  });
  const [match, setMatch] = useState(null);
  // Ambiguous-match cluster. Populated only when the BE returns
  // `ambiguous: true` on the lookup response. We MUST NOT auto-select
  // candidates[0] — that's exactly the bug the BE ambiguity payload
  // exists to prevent. See VIEW_PICK.
  const [candidates, setCandidates] = useState([]);

  // Which date picker modal is open. null when closed; otherwise the field
  // name. Driven by DateButton clicks below.
  const [pickerOpen, setPickerOpen] = useState(null); // null | 'checkIn' | 'checkOut'

  // Timestamp of the last view-change away from a result view (notFound /
  // error) back to the form. Used to absorb iOS Safari "ghost clicks" that
  // fire ~300ms after a touchend — without this, tapping "Try again" would
  // land its delayed synthetic click on the newly-rendered "Find my
  // booking" submit button at the same coordinates and re-fire the lookup.
  const recentBackToFormRef = useRef(0);

  // Reset everything on close. Both "Close" and "No, that's not me" land here.
  useEffect(() => {
    if (!isOpen) {
      setView(VIEW_FORM);
      setMatch(null);
      setCandidates([]);
      setSubmitting(false);
      setForm({ guestName: "", checkInDate: null, checkOutDate: null });
      setPickerOpen(null);
      recentBackToFormRef.current = 0;
    }
  }, [isOpen]);

  const isFormValid =
    form.guestName.trim().length > 0 &&
    !!form.checkInDate &&
    !!form.checkOutDate;

  const handleSubmit = useCallback(
    async (e) => {
      e?.preventDefault?.();
      // Absorb iOS Safari ghost clicks. After tapping "Try again" the view
      // flips to the form and a delayed synthetic click can land on the
      // newly-rendered "Find my booking" button at the same coordinates
      // and fire this submit. 500ms is well beyond the ~300ms ghost-click
      // delay and short enough that an intentional follow-up tap by a
      // human still goes through.
      if (Date.now() - recentBackToFormRef.current < 500) return;
      if (!isFormValid || submitting) return;

      setSubmitting(true);
      try {
        // Public POST endpoint — uses x-api-key only via the unauth helper,
        // same one used for /users/public/:id and /bookings/check-existence.
        const response = await api("/bookings/lookup-by-name", {
          ownerId,
          guestName: form.guestName.trim(),
          checkInDate: toIsoDate(form.checkInDate),
          checkOutDate: toIsoDate(form.checkOutDate),
        });

        // The BE returns two shapes on success:
        //   1) Single hit  → top-level {booking, guest, room, ...}
        //   2) Ambiguous   → same top-level fields (for legacy clients)
        //                    PLUS `ambiguous: true` + `candidates[]`.
        //                    Legacy clients that ignore `ambiguous` still
        //                    auto-select candidates[0] — this branch is
        //                    what actually fixes that case.
        //
        // We check `ambiguous` first so a 2+-candidate response never
        // silently falls through to the single-match confirm view. The
        // BE already trims to a tight name-score cluster (within 0.1 of
        // the best match), so `candidates` here is guaranteed to be
        // genuinely-equivalent bookings the guest must pick between,
        // never a mix of one real match + unrelated fuzzy noise.
        const data = response?.data;
        if (
          response?.success &&
          data?.ambiguous === true &&
          Array.isArray(data.candidates) &&
          data.candidates.length > 1
        ) {
          setCandidates(data.candidates);
          setMatch(null);
          setView(VIEW_PICK);
        } else if (response?.success && data?.booking?.id) {
          setMatch(data);
          setCandidates([]);
          setView(VIEW_CONFIRM);
        } else {
          setView(VIEW_NOT_FOUND);
        }
      } catch (err) {
        const code = err?.response?.status;
        if (code === 404 || code === 400) {
          // 400 (bad input) and 404 (no match) share the same UX — guests
          // can't tell the difference and shouldn't have to.
          setView(VIEW_NOT_FOUND);
        } else {
          setView(VIEW_ERROR);
        }
      } finally {
        setSubmitting(false);
      }
    },
    [isFormValid, submitting, ownerId, form]
  );

  const handleBackToForm = () => {
    // Form values intentionally preserved — guest corrects what they typed.
    // Stamp the ghost-click guard before flipping views so any delayed
    // synthetic click from this tap is absorbed by handleSubmit.
    recentBackToFormRef.current = Date.now();
    setView(VIEW_FORM);
    setMatch(null);
    setCandidates([]);
  };

  const handleConfirm = () => {
    if (!match?.booking?.id) return;
    // ── THE SEAM ──
    // Only the bID crosses. The existing /go entry rehydrates everything
    // authoritatively via /bookings/details/:bID. Path B converges with
    // Path A here, and there is exactly one line where it does so.
    //
    // `autoStart=1` tells LandingV2 to skip its welcome card and fire its
    // existing "Get Started" handler the moment data is ready — guests
    // already saw their property + confirmed on the previous screen, so
    // making them tap a second button would be redundant. Same downstream
    // flow (auth → /bookings/details → onboarding/main-v2 gate), no fork.
    navigate(`/go?bID=${match.booking.id}&autoStart=1`, { replace: true });
  };

  const handlePickCandidate = (candidate) => {
    const bID = candidate?.booking?.id;
    if (!bID) return;
    // Same seam as handleConfirm — the picker itself was the explicit
    // confirmation, so we skip the extra "Is this you?" step. The
    // guest just saw property image + name + dates + reservation name
    // for every candidate and tapped one; showing a second confirm
    // card with the same content would be redundant.
    navigate(`/go?bID=${bID}&autoStart=1`, { replace: true });
  };

  // ─── Sub-views ──────────────────────────────────────────────────────────────

  const renderForm = () => (
    <form onSubmit={handleSubmit} id="lookup-form">
      <VStack spacing={4} align="stretch">
        <Text fontSize="13px" color="gray.400" lineHeight={1.4}>
          Enter the name and dates exactly as they appear on your reservation.
        </Text>

        <FormControl isRequired>
          <FormLabel
            color="gray.300"
            fontSize="13px"
            fontWeight={600}
            mb={1.5}
          >
            Name on reservation
          </FormLabel>
          <Input
            value={form.guestName}
            onChange={(e) =>
              setForm((f) => ({ ...f, guestName: e.target.value }))
            }
            placeholder="e.g. Alan Jones"
            bg="#2d3748"
            border="1px solid"
            borderColor="rgba(255,255,255,0.08)"
            color="white"
            _placeholder={{ color: "gray.500" }}
            _focus={{
              borderColor: "#817EFF",
              boxShadow: "0 0 0 1px #817EFF",
            }}
            autoComplete="name"
            h="48px"
          />
        </FormControl>

        <HStack spacing={3} align="stretch">
          <FormControl isRequired flex={1} minW={0}>
            <FormLabel
              color="gray.300"
              fontSize="13px"
              fontWeight={600}
              mb={1.5}
            >
              Check-in
            </FormLabel>
            <DateButton
              value={formatDateLabel(form.checkInDate)}
              placeholder="Pick a date"
              onClick={() => setPickerOpen("checkIn")}
            />
          </FormControl>

          <FormControl isRequired flex={1} minW={0}>
            <FormLabel
              color="gray.300"
              fontSize="13px"
              fontWeight={600}
              mb={1.5}
            >
              Check-out
            </FormLabel>
            <DateButton
              value={formatDateLabel(form.checkOutDate)}
              placeholder={
                form.checkInDate ? "Pick a date" : "Pick check-in first"
              }
              disabled={!form.checkInDate}
              onClick={() => setPickerOpen("checkOut")}
            />
          </FormControl>
        </HStack>
      </VStack>
    </form>
  );

  const renderConfirm = () => {
    const room = match?.room;
    const guest = match?.guest;
    const booking = match?.booking;
    const imageUrl = buildRoomImageUrl(room?.images?.[0]);
    const showFuzzyDateBanner = match?.exactDateMatch === false;
    const fallbackInitial = (room?.name || guest?.name || "")
      .trim()[0]
      ?.toUpperCase();

    return (
      <VStack spacing={4} align="stretch">
        {imageUrl ? (
          <Image
            src={imageUrl}
            alt={room?.name || "Property"}
            w="100%"
            h="160px"
            objectFit="cover"
            borderRadius="14px"
            fallback={
              <Center
                w="100%"
                h="160px"
                borderRadius="14px"
                bg="rgba(129, 126, 255, 0.12)"
              >
                <Text
                  fontSize="40px"
                  color="#817EFF"
                  className="font-gilroy-bold"
                >
                  {fallbackInitial || "•"}
                </Text>
              </Center>
            }
          />
        ) : (
          <Center
            w="100%"
            h="160px"
            borderRadius="14px"
            bg="rgba(129, 126, 255, 0.12)"
          >
            <Text
              fontSize="40px"
              color="#817EFF"
              className="font-gilroy-bold"
            >
              {fallbackInitial || "•"}
            </Text>
          </Center>
        )}

        <VStack spacing={2.5} align="stretch">
          <Box>
            <Text
              fontSize="11px"
              color="gray.400"
              textTransform="uppercase"
              letterSpacing="0.5px"
              fontWeight={600}
            >
              Property
            </Text>
            <Text
              fontSize="18px"
              color="white"
              fontWeight={700}
              className="font-gilroy-bold"
              lineHeight={1.25}
            >
              {room?.name || "Your stay"}
            </Text>
          </Box>

          <HStack spacing={4} align="flex-start">
            <Box flex={1} minW={0}>
              <Text
                fontSize="11px"
                color="gray.400"
                textTransform="uppercase"
                letterSpacing="0.5px"
                fontWeight={600}
              >
                Reservation
              </Text>
              <Text
                fontSize="14px"
                color="white"
                fontWeight={600}
                noOfLines={1}
              >
                {guest?.name || "—"}
              </Text>
            </Box>

            <Box flex={1.4} minW={0}>
              <Text
                fontSize="11px"
                color="gray.400"
                textTransform="uppercase"
                letterSpacing="0.5px"
                fontWeight={600}
              >
                Stay
              </Text>
              <Text fontSize="14px" color="white" fontWeight={600} noOfLines={1}>
                {formatDateForDisplay(booking?.checkInDate, room?.timezone)}
                {" → "}
                {formatDateForDisplay(booking?.checkOutDate, room?.timezone)}
              </Text>
            </Box>
          </HStack>
        </VStack>

        {showFuzzyDateBanner && (
          <Alert
            status="info"
            bg="rgba(129, 126, 255, 0.12)"
            color="gray.200"
            borderRadius="12px"
            border="1px solid"
            borderColor="rgba(129, 126, 255, 0.3)"
            py={2.5}
          >
            <AlertIcon color="#817EFF" />
            <AlertDescription fontSize="12px" lineHeight={1.4}>
              Dates may look slightly off — we widen the search a day in each
              direction to handle timezone shifts.
            </AlertDescription>
          </Alert>
        )}
      </VStack>
    );
  };

  const renderPick = () => (
    <VStack spacing={3} align="stretch">
      <Text fontSize="13px" color="gray.400" lineHeight={1.4}>
        We found more than one booking that matches. Tap the one that's yours.
      </Text>

      <VStack spacing={2.5} align="stretch">
        {candidates.map((candidate, index) => {
          const c_room = candidate?.room;
          const c_guest = candidate?.guest;
          const c_booking = candidate?.booking;
          // The BE doc mentioned `pictures` for the batched shape; the
          // rest of the app (and single-hit shape) uses `images`.
          // Handle both so we don't fall to a blank thumbnail if the
          // shapes diverge slightly between paths.
          const rawImage =
            c_room?.images?.[0] ?? c_room?.pictures?.[0] ?? null;
          const imageUrl = buildRoomImageUrl(rawImage);
          const fallbackInitial = (c_room?.name || c_guest?.name || "")
            .trim()[0]
            ?.toUpperCase();
          // Stable-ish key. booking.id is present in all successful
          // candidates per the BE contract; index is a defensive
          // fallback so React doesn't warn if the BE ever emits two
          // candidates with the same id.
          const key = c_booking?.id
            ? `${c_booking.id}-${index}`
            : String(index);

          return (
            <Box
              key={key}
              as="button"
              type="button"
              onClick={() => handlePickCandidate(candidate)}
              textAlign="left"
              w="full"
              bg="#2d3748"
              borderRadius="14px"
              border="1px solid"
              borderColor="rgba(255,255,255,0.08)"
              px={3}
              py={3}
              transition="all 0.15s ease"
              _hover={{
                bg: "#374152",
                borderColor: "rgba(129, 126, 255, 0.45)",
              }}
              _active={{ transform: "scale(0.99)" }}
              _focusVisible={{
                outline: "none",
                borderColor: "#817EFF",
                boxShadow: "0 0 0 1px #817EFF",
              }}
            >
              <HStack spacing={3} align="center">
                {imageUrl ? (
                  <Image
                    src={imageUrl}
                    alt={c_room?.name || "Property"}
                    w="64px"
                    h="64px"
                    objectFit="cover"
                    borderRadius="10px"
                    flexShrink={0}
                    fallback={
                      <Center
                        w="64px"
                        h="64px"
                        borderRadius="10px"
                        bg="rgba(129, 126, 255, 0.12)"
                        flexShrink={0}
                      >
                        <Text
                          fontSize="24px"
                          color="#817EFF"
                          className="font-gilroy-bold"
                        >
                          {fallbackInitial || "•"}
                        </Text>
                      </Center>
                    }
                  />
                ) : (
                  <Center
                    w="64px"
                    h="64px"
                    borderRadius="10px"
                    bg="rgba(129, 126, 255, 0.12)"
                    flexShrink={0}
                  >
                    <Text
                      fontSize="24px"
                      color="#817EFF"
                      className="font-gilroy-bold"
                    >
                      {fallbackInitial || "•"}
                    </Text>
                  </Center>
                )}

                <VStack spacing={1} align="stretch" flex={1} minW={0}>
                  <Text
                    fontSize="15px"
                    color="white"
                    fontWeight={700}
                    className="font-gilroy-bold"
                    noOfLines={1}
                  >
                    {c_room?.name || "Your stay"}
                  </Text>
                  <Text
                    fontSize="12px"
                    color="gray.400"
                    fontWeight={500}
                    noOfLines={1}
                  >
                    {formatDateForDisplay(
                      c_booking?.checkInDate,
                      c_room?.timezone
                    )}
                    {" → "}
                    {formatDateForDisplay(
                      c_booking?.checkOutDate,
                      c_room?.timezone
                    )}
                  </Text>
                  {c_guest?.name && (
                    <Text
                      fontSize="11px"
                      color="gray.500"
                      fontWeight={500}
                      noOfLines={1}
                    >
                      Reserved by {c_guest.name}
                    </Text>
                  )}
                </VStack>
              </HStack>
            </Box>
          );
        })}
      </VStack>
    </VStack>
  );

  const renderNotFound = () => (
    <VStack spacing={3} align="stretch" textAlign="center" py={4}>
      <Text
        fontSize="20px"
        color="white"
        fontWeight={700}
        className="font-gilroy-bold"
      >
        We couldn't find your booking
      </Text>
      <Text fontSize="14px" color="gray.400" px={2} lineHeight={1.5}>
        Double-check the name and dates on your reservation, or contact your
        host for help.
      </Text>
    </VStack>
  );

  const renderError = () => (
    <VStack spacing={3} align="stretch" textAlign="center" py={4}>
      <Text
        fontSize="20px"
        color="white"
        fontWeight={700}
        className="font-gilroy-bold"
      >
        Something went wrong
      </Text>
      <Text fontSize="14px" color="gray.400" px={2} lineHeight={1.5}>
        Couldn't reach the lookup service. Please try again in a moment.
      </Text>
    </VStack>
  );

  // ─── Footer per view ───────────────────────────────────────────────────────
  // The CTA(s) live in DrawerFooter so they stick to the bottom of the
  // drawer regardless of body scroll.

  const primaryButtonStyle = {
    bg: "#817EFF",
    color: "white",
    h: "52px",
    w: "full",
    borderRadius: "60px",
    fontWeight: 700,
    fontSize: "15px",
    _hover: { bg: "#504CFF" },
    _disabled: {
      opacity: 0.5,
      cursor: "not-allowed",
      bg: "#817EFF",
      pointerEvents: "none",
    },
  };

  const renderFooter = () => {
    if (view === VIEW_FORM) {
      return (
        <Button
          type="submit"
          form="lookup-form"
          isLoading={submitting}
          loadingText="Searching"
          isDisabled={!isFormValid || submitting}
          {...primaryButtonStyle}
        >
          Find my booking
        </Button>
      );
    }

    if (view === VIEW_CONFIRM) {
      return (
        <VStack spacing={2} w="full">
          <Button type="button" onClick={handleConfirm} {...primaryButtonStyle}>
            Yes, that's me
          </Button>
          <Button
            type="button"
            variant="ghost"
            color="gray.400"
            fontWeight={600}
            fontSize="13px"
            h="40px"
            onClick={onClose}
            _hover={{ bg: "rgba(255,255,255,0.04)", color: "gray.200" }}
          >
            No, that's not me
          </Button>
        </VStack>
      );
    }

    if (view === VIEW_PICK) {
      // Only escape hatch — each candidate card is its own primary
      // action, so no confirm button here. "Not seeing your booking?"
      // bounces back to the form (values preserved) so the guest can
      // tweak name/dates and re-search.
      return (
        <Button
          type="button"
          variant="ghost"
          color="gray.400"
          fontWeight={600}
          fontSize="13px"
          h="40px"
          w="full"
          onClick={handleBackToForm}
          _hover={{ bg: "rgba(255,255,255,0.04)", color: "gray.200" }}
        >
          Not seeing your booking?
        </Button>
      );
    }

    if (view === VIEW_NOT_FOUND) {
      return (
        <Button type="button" onClick={handleBackToForm} {...primaryButtonStyle}>
          Try again
        </Button>
      );
    }

    if (view === VIEW_ERROR) {
      // Same UX as the no-match path: bounce back to the form with values
      // preserved so the guest can either re-submit as-is or correct
      // something before retrying.
      return (
        <Button type="button" onClick={handleBackToForm} {...primaryButtonStyle}>
          Try again
        </Button>
      );
    }

    return null;
  };

  // Calendar modal change handler — branches by which date the modal is
  // currently editing. Closes itself on selection.
  const handlePickerChange = (date) => {
    if (pickerOpen === "checkIn") {
      setForm((f) => ({
        ...f,
        checkInDate: date,
        // If the new check-in is after the existing check-out, clear it
        // so the form can't submit an invalid range.
        checkOutDate:
          f.checkOutDate && date && f.checkOutDate < date
            ? null
            : f.checkOutDate,
      }));
    } else if (pickerOpen === "checkOut") {
      setForm((f) => ({ ...f, checkOutDate: date }));
    }
    setPickerOpen(null);
  };

  return (
    <>
      <Drawer isOpen={isOpen} placement="bottom" onClose={onClose}>
        <DrawerOverlay />
        <DrawerContent
          bg="#1a202c"
          borderTopRadius="20px"
          // Auto-fit content but cap so confirm view (image + info) doesn't
          // run off the top of the viewport on small screens.
          sx={{
            height: "auto",
            maxHeight: "85vh",
          }}
          maxW={{ base: "100%", md: "560px" }}
          mx="auto"
          mb={{ md: 8 }}
          borderRadius={{ md: "24px" }}
        >
          <DrawerHeader
            borderBottomWidth="1px"
            borderBottomColor="#2d3748"
            display="flex"
            alignItems="center"
            justifyContent="space-between"
            pr={4}
            py={3}
          >
            <Text fontSize="15px" fontWeight={700} color="white">
              {HEADER_BY_VIEW[view]}
            </Text>
            <Button
              onClick={onClose}
              size="sm"
              color="#a89fff"
              bg="rgba(129, 126, 255, 0.12)"
              fontSize="12px"
              fontWeight={600}
              borderRadius="8px"
              border="1px solid"
              borderColor="rgba(129, 126, 255, 0.3)"
              px={3}
              h="30px"
              _hover={{ bg: "rgba(129, 126, 255, 0.22)" }}
              _active={{ transform: "scale(0.97)" }}
              transition="all 0.15s ease"
            >
              Close
            </Button>
          </DrawerHeader>

          <DrawerBody px={5} py={5}>
            {view === VIEW_FORM && renderForm()}
            {view === VIEW_CONFIRM && renderConfirm()}
            {view === VIEW_PICK && renderPick()}
            {view === VIEW_NOT_FOUND && renderNotFound()}
            {view === VIEW_ERROR && renderError()}
          </DrawerBody>

          <DrawerFooter
            px={5}
            pt={3}
            pb={5}
            borderTopWidth="1px"
            borderTopColor="#2d3748"
          >
            {renderFooter()}
          </DrawerFooter>
        </DrawerContent>
      </Drawer>

      {/* Calendar lives in a sibling Modal — Chakra's modal manager stacks
          this above the Drawer regardless of CSS z-index quirks, which is
          what react-datepicker's withPortal failed to do. */}
      <PickerModal
        isOpen={pickerOpen === "checkIn"}
        onClose={() => setPickerOpen(null)}
        selected={form.checkInDate}
        onChange={handlePickerChange}
      />
      <PickerModal
        isOpen={pickerOpen === "checkOut"}
        onClose={() => setPickerOpen(null)}
        selected={form.checkOutDate}
        onChange={handlePickerChange}
        minDate={form.checkInDate || undefined}
      />
    </>
  );
};

export default LookupDrawer;
